import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild, ElementRef, ViewContainerRef, ComponentRef, HostListener } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { HttpClient } from '@angular/common/http';
import { catchError, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import { InvoiceTemplateService } from './invoice-template.service';
import { baseUrl } from '../../../Data/service/global.service';

interface DocumentData {
	pdfLink: string;
	rawPdfUrl: string;
	pdfUrl: SafeResourceUrl | null;
	documentNumber?: string;
	documentHtmlContent?: string;
	documentId?: string;
	documentLanguage?: string;
}

interface SimpleDocumentData {
	documentId: string | number;
	documentNumber?: string;
	documentLanguage?: string;
	hasAllocationDenial?: boolean;
	documentData?: any;
}

@Component({
	selector: 'app-alldocuments-view-pdf-modal',
	templateUrl: './alldocuments-view-pdf-modal.component.html',
	styleUrls: ['./alldocuments-view-pdf-modal.component.scss']
})
export class AlldocumentsViewPdfModalComponent implements OnInit, OnDestroy {
	@Output() pdfLoaded = new EventEmitter<void>();
	@Output() languageChanged = new EventEmitter<string>();
	@Output() allocationDenialActionsRequested = new EventEmitter<any>();

	@ViewChild('pdfIframe') pdfIframe: ElementRef | null = null;
	@ViewChild('templateContainer', { read: ViewContainerRef }) templateContainer: ViewContainerRef | null = null;
	@ViewChild('shareTriggerBtn') shareTriggerBtn?: ElementRef<HTMLButtonElement>;

	displayDialog: boolean = false;
	PdfLink: string = '';
	rawPdfUrl: string = '';
	pdfUrl: SafeResourceUrl | null = null;
	curentLang: string = '';
	documentHtmlContent: string = '';
	documentNumber: string = '';
	pdfLoading: boolean = true;
	selectedLanguage: string = '';
	shareMenuItems: MenuItem[] = [];

	documentId: string = '';
	isChangingDocumentLanguage: boolean = false;

	hasAllocationDenial: boolean = false;
	currentDocumentData: any = null;

	iframeKey: string = Date.now().toString();

	availableLanguages = [
		{ code: 'en', name: 'English' },
		{ code: 'ar', name: 'العربية' },
		{ code: 'he', name: 'עברית' }
	];

	isDownloading: boolean = false;
	originalDocumentLanguage: string = '';
	showMobileShareSheet = false;
	showShareDropdown = false;
	shareDropdownOpensBelow = false;
	shareDropdownAlignEnd = false;
	
	// Email dialog properties
	showEmailDialog: boolean = false;
	customerEmailInput: string = '';
	saveEmailPermanently: boolean = false;
	private pendingInvoiceId: number | null = null;
	
	private currentTemplateComponent: ComponentRef<any> | null = null;
	private maximizedOnce: boolean = false;
	private _injectedInvoiceIframe: HTMLIFrameElement | null = null;
	private _dialogRepositionListener: ((evt?: any) => void) | null = null;
	private _appliedContentShift: number = 0;
	private _appliedDialogShift: number = 0;
	private _isInitializing: boolean = false;
	showLanguageSelector = true;
	private localTranslations: any = null;
	// reposition handler removed to avoid forcing dialog frame positioning

	emailForm: FormGroup;

	constructor(
		private messageService: MessageService,
		private sanitizer: DomSanitizer,
		private translateService: TranslateService,
		private http: HttpClient,
		private httpHelpService: HttpHelpService,
		private invoiceTemplateService: InvoiceTemplateService,
		private fb: FormBuilder
	) {
		this.curentLang = localStorage.getItem('curentLang') || 'en';
		this.selectedLanguage = this.curentLang;
		
		// Initialize email form
		this.emailForm = this.fb.group({
			email: ['', [Validators.required, Validators.email]],
			savePermanently: [false]
		});
	}

	/**
	 * Apply inline styles (with !important) to key invoice elements to ensure
	 * the intended desktop colors are used even if external stylesheets try to override them.
	 * This is a defensive measure to get visual parity while we find a cleaner override.
	 */
	private applyInvoiceInlineStyles(): void {
		try {
			const hostInner = document.querySelector('.template-host-inner') as HTMLElement | null;
			// If we injected an isolation iframe, apply styles inside it instead of the parent document
			if (this._injectedInvoiceIframe && this._injectedInvoiceIframe.contentDocument) {
				const idoc = this._injectedInvoiceIframe.contentDocument as Document;
				const pageInIframe = idoc.querySelector('.page') as HTMLElement | null;
				if (!pageInIframe) return;
				// helper to set inline style with !important inside iframe
				const setImportantIframe = (el: HTMLElement, prop: string, value: string) => {
					try {
						(el.style as any).setProperty(prop, value, 'important');
					} catch (e) { /* ignore */ }
				};

				setImportantIframe(pageInIframe, '--primary', '#e12027');
				setImportantIframe(pageInIframe, '--primary-ink', '#ffffff');
				setImportantIframe(pageInIframe, '--ink', '#1a1a1a');
				setImportantIframe(pageInIframe, '--muted', '#6b6b6b');
				setImportantIframe(pageInIframe, '--border', '#e6e6e6');
				setImportantIframe(pageInIframe, '--panel', '#f2f2f2');

				setImportantIframe(pageInIframe, 'background', '#ffffff');
				setImportantIframe(pageInIframe, 'color', '#1a1a1a');

				const totalsBoxIf = idoc.querySelector('.totals .box') as HTMLElement | null;
				if (totalsBoxIf) {
					setImportantIframe(totalsBoxIf, 'background', '#f2f2f2');
					setImportantIframe(totalsBoxIf, 'border', '1px solid #e6e6e6');
					setImportantIframe(totalsBoxIf, 'color', '#1a1a1a');
				}

				const toolbarBtnsIf = idoc.querySelectorAll('.toolbar .btn') as NodeListOf<HTMLElement>;
				toolbarBtnsIf.forEach(b => {
					setImportantIframe(b, 'background', '#ffffff');
					setImportantIframe(b, 'color', '#1a1a1a');
					setImportantIframe(b, 'border', '1px solid #e6e6e6');
					setImportantIframe(b, 'box-shadow', 'none');
				});

				const thsIf = idoc.querySelectorAll('table thead th') as NodeListOf<HTMLElement>;
				thsIf.forEach(th => {
					setImportantIframe(th, 'background', '#e12027');
					setImportantIframe(th, 'color', '#ffffff');
					setImportantIframe(th, 'border-color', '#e12027');
				});

				// Inject a late style block into the iframe head to further harden appearance
				try {
					const iframeHead = idoc.head || idoc.getElementsByTagName('head')[0];
					if (iframeHead) {
						let s = idoc.getElementById('invoice-force-styles');
						if (s) s.remove();
						const styleEl = idoc.createElement('style');
						styleEl.id = 'invoice-force-styles';
						styleEl.type = 'text/css';
						styleEl.appendChild(idoc.createTextNode(`
						.page { background: #ffffff !important; color: #1a1a1a !important; }
						.page thead th { background: #e12027 !important; color: #ffffff !important; border-color: #e12027 !important; }
						.page .totals .box { background: #f2f2f2 !important; border: 1px solid #e6e6e6 !important; color: #1a1a1a !important; }
						.page .toolbar .btn { background: #ffffff !important; color: #1a1a1a !important; border: 1px solid #e6e6e6 !important; }
						`));
						iframeHead.appendChild(styleEl);
					}
				} catch (e) { /* ignore */ }

				return;
			}

			if (!hostInner) return;
			const page = hostInner.querySelector('.page') as HTMLElement | null;
			if (!page) return;

			// helper to set inline style with !important
			const setImportant = (el: HTMLElement, prop: string, value: string) => {
				try {
					(el.style as any).setProperty(prop, value, 'important');
				} catch (e) { /* ignore */ }
			};

			// Set CSS variables on the actual injected page element
			setImportant(page, '--primary', '#e12027');
			setImportant(page, '--primary-ink', '#ffffff');
			setImportant(page, '--ink', '#1a1a1a');
			setImportant(page, '--muted', '#6b6b6b');
			setImportant(page, '--border', '#e6e6e6');
			setImportant(page, '--panel', '#f2f2f2');

			// Ensure page background is white
			setImportant(page, 'background', '#ffffff');
			setImportant(page, 'color', '#1a1a1a');

			// Apply to totals box
			const totalsBox = page.querySelector('.totals .box') as HTMLElement | null;
			if (totalsBox) {
				setImportant(totalsBox, 'background', '#f2f2f2');
				setImportant(totalsBox, 'border', '1px solid #e6e6e6');
				setImportant(totalsBox, 'color', '#1a1a1a');
			}

			// Toolbar buttons
			const toolbarBtns = page.querySelectorAll('.toolbar .btn') as NodeListOf<HTMLElement>;
			toolbarBtns.forEach(b => {
				setImportant(b, 'background', '#ffffff');
				setImportant(b, 'color', '#1a1a1a');
				setImportant(b, 'border', '1px solid #e6e6e6');
				setImportant(b, 'box-shadow', 'none');
			});

			// Table headers
			const ths = page.querySelectorAll('table thead th') as NodeListOf<HTMLElement>;
			ths.forEach(th => {
				setImportant(th, 'background', '#e12027');
				setImportant(th, 'color', '#ffffff');
				setImportant(th, 'border-color', '#e12027');
			});

			// Inject a late <style> into the document head with very specific selectors
			// This helps override global/theme styles that are applied after component styles.
			try {
				const existing = document.getElementById('invoice-force-styles');
				if (existing) existing.remove();
				const styleEl = document.createElement('style');
				styleEl.id = 'invoice-force-styles';
				styleEl.type = 'text/css';
				const css = `
					.pdf-viewer-dialog .p-dialog-content > .template-host > .template-host-inner .page {
					  background: #ffffff !important;
					  color: #1a1a1a !important;
					}
					.pdf-viewer-dialog .p-dialog-content > .template-host > .template-host-inner .page thead th {
					  background: #e12027 !important;
					  color: #ffffff !important;
					  border-color: #e12027 !important;
					}
					.pdf-viewer-dialog .p-dialog-content > .template-host > .template-host-inner .page .totals .box {
					  background: #f2f2f2 !important;
					  border: 1px solid #e6e6e6 !important;
					  color: #1a1a1a !important;
					}
					.pdf-viewer-dialog .p-dialog-content > .template-host > .template-host-inner .page .toolbar .btn {
					  background: #ffffff !important;
					  color: #1a1a1a !important;
					  border: 1px solid #e6e6e6 !important;
					}
					.pdf-viewer-dialog .p-dialog-content > .template-host > .template-host-inner .page .toolbar .btn.red {
					  background: #ffffff !important;
					  color: #e12027 !important;
					  border: 2px solid #e12027 !important;
					}
					`; 
				styleEl.appendChild(document.createTextNode(css));
				document.head.appendChild(styleEl);
			} catch (e) {
				// If document.head manipulation fails, ignore — inline styles still applied
				console.warn('[InvoiceDiag] failed to inject invoice-force-styles', e);
			}
		} catch (err) {
			console.warn('[InvoiceDiag] applyInvoiceInlineStyles error', err);
		}
	}

	/**
	 * Clear inline positioning styles that may have been applied programmatically by previous runs.
	 * This helps CSS centering/flow to take effect inside PrimeNG dialogs.
	 */
	private clearInlineDialogStyles(): void {
		try {
			const dialogRoot = document.querySelector('.pdf-viewer-dialog') as HTMLElement | null;
			if (dialogRoot) {
				// Only clear the specific inline properties we suspect were interfering
				if (dialogRoot.style.left) dialogRoot.style.left = '';
				if (dialogRoot.style.transform) dialogRoot.style.transform = '';
				if (dialogRoot.style.width && dialogRoot.style.width.includes('px')) dialogRoot.style.width = '';
			}

			// Clear styles on the host wrapper where the template is injected
			const hostInner = document.querySelector('.template-host-inner') as HTMLElement | null;
			if (hostInner) {
				if (hostInner.style.left) hostInner.style.left = '';
				if (hostInner.style.transform) hostInner.style.transform = '';
				if (hostInner.style.width && hostInner.style.width.includes('px')) hostInner.style.width = '';
			}

			// Also clear any inline styles on the invoice container(s)
			document.querySelectorAll('.invoice-container').forEach((el) => {
				const e = el as HTMLElement;
				if (e.style.left) e.style.left = '';
				if (e.style.transform) e.style.transform = '';
				if (e.style.width && e.style.width.includes('px')) e.style.width = '';
			});
		} catch (err) {
			console.warn('clearInlineDialogStyles failed', err);
		}
	}

	/**
	 * Diagnostic helper: when called it will locate the injected invoice root
	 * and log computed styles for key selectors and any matching stylesheet rules.
	 * This is best-effort and will skip cross-origin stylesheets.
	 */
	private logInvoiceStyleDiagnostics(): void {
		try {
			const hostInner = document.querySelector('.template-host-inner') as HTMLElement | null;
			if (!hostInner) {
				console.warn('[InvoiceDiag] template host not found');
				return;
			}
			try { const preview = hostInner.innerHTML && hostInner.innerHTML.length > 0 ? hostInner.innerHTML.slice(0, 500) : '<empty>'; } catch (e) { }

			// First try the obvious root markers
			let invoiceRoot = hostInner.querySelector('.page') as HTMLElement | null;
			if (!invoiceRoot) invoiceRoot = hostInner.firstElementChild as HTMLElement | null;

			// If still not found, try alternate heuristics:
			if (!invoiceRoot) {
				console.warn('[InvoiceDiag] invoice root not found inside .template-host-inner — trying alternate searches');
				// 1) look for element containing Arabic word for invoice (فاتورة)
				const textMatch = Array.from(hostInner.querySelectorAll('*')).find(el => {
					try { return (el.textContent || '').includes('فاتورة'); } catch (e) { return false; }
				});
				if (textMatch) {
					invoiceRoot = (textMatch.closest('div') as HTMLElement) || (textMatch as HTMLElement);
					}

				// 2) look for table headers that match invoice tables
				if (!invoiceRoot) {
					const th = hostInner.querySelector('table thead th') as HTMLElement | null;
					if (th) {
						invoiceRoot = (th.closest('table') as HTMLElement) || (th as HTMLElement);
						}
				}

				// 3) if hostInner is empty or didn't contain it, search inside the dialog content as fallback
				if (!invoiceRoot) {
					const dialogContent = document.querySelector('.p-dialog .p-dialog-content') as HTMLElement | null;
					if (dialogContent) {
						const fuzzy = Array.from(dialogContent.querySelectorAll('*')).find(el => {
							try { return (el.textContent || '').includes('فاتورة') || el.querySelector && el.querySelector('table thead th'); } catch (e) { return false; }
						});
						if (fuzzy) {
							invoiceRoot = (fuzzy.closest('div') as HTMLElement) || (fuzzy as HTMLElement);
							}
					}
				}

				if (!invoiceRoot) {
					console.warn('[InvoiceDiag] still could not locate invoice root — giving up for now');
					return;
				}
			}

			const selectors = [
				'.page',
				'.page .toolbar .btn',
				'table thead th',
				'.totals .box'
			];

			console.group('[InvoiceDiag] Invoice style diagnostics');
			selectors.forEach(sel => {
				try {
					const el = invoiceRoot.querySelector(sel) as HTMLElement | null || document.querySelector(sel) as HTMLElement | null;
					if (!el) {
						return;
					}
					const cs = window.getComputedStyle(el);
					console.group(`[InvoiceDiag] ${sel}`);
					// Search document stylesheets for matching rules (best-effort)
					const matchingRules: Array<{href: string | null, cssText: string}> = [];
					for (let i = 0; i < document.styleSheets.length; i++) {
						const sheet = document.styleSheets[i] as CSSStyleSheet;
						try {
							const rules = sheet.cssRules;
							for (let j = 0; j < rules.length; j++) {
								const rule = rules[j] as CSSStyleRule;
								if (!rule || !('selectorText' in rule)) continue;
								const selText = (rule as any).selectorText as string;
								if (selText && (selText === sel || selText.includes(sel) || selText.split(',').map(s=>s.trim()).includes(sel))) {
									matchingRules.push({ href: sheet.href || null, cssText: rule.cssText });
								}
							}
						} catch (err) {
							// Could be cross-origin stylesheet - note and continue
							//continue;
						}
					}
					console.groupEnd();
				} catch (err) {
					console.warn('[InvoiceDiag] error while processing selector', sel, err);
				}
			});

			console.groupEnd();
		} catch (err) {
			console.error('[InvoiceDiag] unexpected error:', err);
		}
	}

	/**
	 * @description Angular lifecycle hook called after every check of the component's view.
	 * Used here to maximize the dialog automatically on small screens when it first opens.
	 */
	ngAfterViewChecked(): void {
		if (
			this.displayDialog &&
			window.innerWidth < 768 &&
			!this.maximizedOnce
		) {
			this.clickMaximizeButton();
			this.maximizedOnce = true;
		}
	}

	/**
	 * @protected
	 * @description Programmatically clicks the maximize button of the PrimeNG dialog.
	 * Used for responsive behavior on small screens.
	 */
	protected clickMaximizeButton() {
		const dialogEl = document.querySelector('.p-dialog') as HTMLElement;
		if (dialogEl) {
			const maximizeBtn = dialogEl.querySelector('.p-dialog-header-maximize') as HTMLElement;
			if (maximizeBtn) {
				maximizeBtn.click();
			}
		}
	}

	ngOnInit() {
		this.initShareMenuItems();

		// keep existing behavior for resize (no forced dialog repositioning)
	}

	ngOnDestroy() {
		this.closeShareMenu();
		this.cleanupTemplateComponent();
		// remove any reposition listener we installed
		if (this._dialogRepositionListener) {
			window.removeEventListener('resize', this._dialogRepositionListener as any);
			this._dialogRepositionListener = null;
		}

		// remove temporary visual guide if present
		// (visual guide removed earlier) no DOM cleanup needed for it

		// remove any content shift we applied
		try {
			const invoiceEl = document.querySelector('.template-host .invoice-content') as HTMLElement | null;
			if (invoiceEl && this._appliedContentShift) {
				invoiceEl.style.transform = '';
				invoiceEl.style.transition = '';
				this._appliedContentShift = 0;
			}
		} catch (e) {
			// ignore
		}

		// revert any dialog shift we applied
		try {
			const dialogRoot = document.querySelector('.pdf-viewer-dialog') as HTMLElement | null;
			if (dialogRoot && this._appliedDialogShift) {
				const prevLeft = dialogRoot.offsetLeft - this._appliedDialogShift;
				dialogRoot.style.left = prevLeft + 'px';
				this._appliedDialogShift = 0;
			}
		} catch (e) {
			// ignore
		}
	}

	/**
	 * Helper method to clear existing toasts before showing a new one
	 * This prevents multiple toasts from appearing simultaneously
	 */
	private clearAndAddToast(severity: string, summary: string, detail: string, life?: number): void {
		// Only clear existing non-sticky toasts for transient notifications
		const toast: any = { severity, summary, detail };
		
		// Check if life parameter was explicitly provided (not undefined)
		const hasExplicitLife = life !== undefined;
		
		// If life is explicitly provided, always make it non-sticky and auto-dismiss
		if (hasExplicitLife) {
			this.messageService.clear();
			toast.life = life;
			toast.sticky = false; // Explicitly set to non-sticky - this is critical!
		} else {
			// No explicit life: use default behavior
			if (this.isMobileDevice()) {
				// On mobile devices, make all notifications auto-dismiss
				this.messageService.clear();
				toast.life = 3000;
				toast.sticky = false;
			} else {
				// On desktop, keep success/error sticky for better UX
				if (severity === 'success' || severity === 'error') {
					// Default behavior: sticky for success/error when no explicit life
					toast.sticky = true;
				} else {
					// Non-success/error: auto-dismiss after default time
					this.messageService.clear();
					toast.life = 3000;
					toast.sticky = false;
				}
			}
		}

		this.messageService.add(toast);
	}

	/** Resolve i18n keys to plain strings for PrimeNG menu models. */
	private shareMenuLabel(key: string): string {
		const value = this.translateService.instant(key);
		return typeof value === 'string' ? value : key;
	}

	/**
	 * Initialize the share menu items for the popup menu.
	 */
	initShareMenuItems(): void {
		this.shareMenuItems = [
			{
				label: this.shareMenuLabel('allDocuments.modal.download'),
				icon: 'pi pi-download',
				command: () => this.downloadOriginalDocPdfFromLink(),
			},
			{
				label: this.shareMenuLabel('allDocuments.modal.copyLink'),
				icon: 'pi pi-copy',
				command: () => this.copyPdfLinkToClipboard(),
			},
			{
				label: this.shareMenuLabel('allDocuments.modal.shareWhatsapp'),
				icon: 'pi pi-whatsapp',
				command: () => this.shareViaWhatsApp(),
			},
			{
				label: this.shareMenuLabel('allDocuments.modal.shareEmail'),
				icon: 'pi pi-envelope',
				command: () => this.shareViaEmail(),
			},
		];
	}

	openShareMenu(event: Event): void {
		event.preventDefault();
		event.stopPropagation();
		this.initShareMenuItems();
		if (this.isMobileDevice()) {
			this.closeShareMenu();
			this.showMobileShareSheet = true;
			return;
		}
		this.showShareDropdown = !this.showShareDropdown;
		if (this.showShareDropdown) {
			requestAnimationFrame(() => {
				this.updateShareDropdownPlacement();
				requestAnimationFrame(() => this.updateShareDropdownPlacement());
			});
			window.addEventListener('resize', this.shareDropdownResizeHandler);
		} else {
			this.resetShareDropdownPlacement();
			window.removeEventListener('resize', this.shareDropdownResizeHandler);
		}
	}

	closeShareMenu(): void {
		this.showShareDropdown = false;
		this.showMobileShareSheet = false;
		this.resetShareDropdownPlacement();
		window.removeEventListener('resize', this.shareDropdownResizeHandler);
	}

	private shareDropdownResizeHandler = (): void => {
		if (this.showShareDropdown) {
			this.updateShareDropdownPlacement();
		}
	};

	private resetShareDropdownPlacement(): void {
		this.shareDropdownOpensBelow = false;
		this.shareDropdownAlignEnd = false;
	}

	private updateShareDropdownPlacement(): void {
		const trigger = this.shareTriggerBtn?.nativeElement;
		const panel = document.querySelector(
			'.pdf-viewer-dialog .share-dropdown-panel'
		) as HTMLElement | null;
		if (!trigger || !panel) {
			return;
		}

		const padding = 8;
		const gap = 8;
		const rect = trigger.getBoundingClientRect();
		const panelHeight = panel.offsetHeight;
		const panelWidth = panel.offsetWidth;
		const isRtl = getComputedStyle(document.documentElement).direction === 'rtl';

		this.shareDropdownOpensBelow = rect.top - panelHeight - gap < padding;

		if (isRtl) {
			this.shareDropdownAlignEnd = rect.right - panelWidth < padding;
		} else {
			this.shareDropdownAlignEnd = rect.left + panelWidth > window.innerWidth - padding;
		}
	}

	@HostListener('document:keydown.escape')
	onShareEscapeKey(): void {
		if (this.showShareDropdown || this.showMobileShareSheet) {
			this.closeShareMenu();
		}
	}

	runShareMenuAction(item: MenuItem): void {
		if (item.command) {
			item.command({ originalEvent: {} as Event, item });
		}
		this.closeShareMenu();
	}

	/**
	 * Copy PDF link to clipboard with current language
	 */
	copyPdfLinkToClipboard(): void {
		const pdfUrl = this.getPdfUrlForSharing();
		if (pdfUrl) {
			this.copyLinkToClipboard(pdfUrl);
		} else {
			this.clearAndAddToast(
				'error',
				this.translateService.instant('common.error'),
				'No document available to share'
			);
		}
	}

	/**
	 * Share via WhatsApp with current language
	 */
	shareViaWhatsApp(): void {
		const pdfUrl = this.getPdfUrlForSharing();
		if (pdfUrl) {
			const message = this.documentNumber 
				? `Invoice ${this.documentNumber} - ${this.getCurrentLanguageName()}`
				: `Document - ${this.getCurrentLanguageName()}`;
			window.open(`https://wa.me/?text=${encodeURIComponent(message + ' ' + pdfUrl)}`, '_blank');
		} else {
			this.clearAndAddToast(
				'error',
				this.translateService.instant('common.error'),
				'No document available to share'
			);
		}
	}

	/**
	 * Share via Email with current language
	 * Calls the API to send invoice to customer email
	 */
	shareViaEmail(): void {
		if (!this.documentId) {
			this.clearAndAddToast(
				'error',
				this.translateService.instant('common.error'),
				'No document available to share'
			);
			return;
		}

		const invoiceId = Number(this.documentId);
		if (!invoiceId || isNaN(invoiceId)) {
			this.clearAndAddToast(
				'error',
				this.translateService.instant('common.error'),
				'Invalid invoice ID'
			);
			return;
		}

		// Show loading state
		this.isDownloading = true;

		this.httpHelpService.sendInvoiceToCustomerEmail(invoiceId).subscribe({
			next: (response: any) => {
				this.isDownloading = false;
				
				// Handle response - check if success is true or false
				const isSuccess = response?.success === true;
				const message = response?.message || (isSuccess ? 'Email sent successfully' : 'Failed to send email');
				
				// Check if the error message indicates customer has no email
				if (!isSuccess && (message === 'لا يوجد إيميل للعميل' || message.includes('لا يوجد إيميل') || message.includes('no email'))) {
					// Show email dialog
					this.pendingInvoiceId = invoiceId;
					this.showEmailDialog = true;
					this.emailForm.reset({
						email: '',
						savePermanently: false
					});
					return;
				}
				
				const severity = isSuccess ? 'success' : 'error';
				
				this.clearAndAddToast(
					severity,
					severity === 'success' 
						? this.translateService.instant('common.success')
						: this.translateService.instant('common.error'),
					message,
					3000 // Auto-dismiss after 3 seconds
				);
			},
			error: (error: any) => {
				this.isDownloading = false;
				
				// Try to get error message from response body
				// API might return error with message in error.error.message
				const errorMessage = error?.error?.message || error?.message || 'Failed to send email';
				
				// Check if the error message indicates customer has no email
				if (errorMessage === 'لا يوجد إيميل للعميل' || errorMessage.includes('لا يوجد إيميل') || errorMessage.includes('no email')) {
					// Show email dialog
					this.pendingInvoiceId = invoiceId;
					this.showEmailDialog = true;
					this.emailForm.reset({
						email: '',
						savePermanently: false
					});
					return;
				}
				
				this.clearAndAddToast(
					'error',
					this.translateService.instant('common.error'),
					errorMessage,
					3000 // Auto-dismiss after 3 seconds
				);
			}
		});
	}

	/**
	 * Handle sending email from the dialog
	 */
	onSendEmailFromDialog(): void {
		if (this.emailForm.invalid) {
			this.emailForm.markAllAsTouched();
			return;
		}

		const email = this.emailForm.get('email')?.value;
		const savePermanently = this.emailForm.get('savePermanently')?.value;

		if (!email || !this.pendingInvoiceId) {
			return;
		}

		// Get customer ID from current document data
		const customerId = this.currentDocumentData?.customer_id || this.currentDocumentData?.customer?.customer_id;
		
		if (!customerId) {
			this.clearAndAddToast(
				'error',
				this.translateService.instant('common.error'),
				'Customer ID not found',
				3000 // Auto-dismiss after 3 seconds
			);
			this.showEmailDialog = false;
			return;
		}

		// If checkbox is checked (save permanently): Update customer first, then send invoice without tempEmail
		// If checkbox is unchecked (temporary): Send invoice with tempEmail, don't update customer
		if (savePermanently) {
			this.updateCustomerEmailAndSendInvoice(customerId, email, this.pendingInvoiceId);
		} else {
			// Temporary email: Send invoice with tempEmail in payload, don't update customer
			this.sendInvoiceWithTempEmail(this.pendingInvoiceId, email);
		}
	}

	/**
	 * Send invoice with temporary email (without updating customer)
	 */
	private sendInvoiceWithTempEmail(invoiceId: number, tempEmail: string): void {
		this.isDownloading = true;
		
		this.httpHelpService.sendInvoiceToCustomerEmail(invoiceId, tempEmail).subscribe({
			next: (response: any) => {
				this.isDownloading = false;
				this.showEmailDialog = false;
				this.pendingInvoiceId = null;
				
				const isSuccess = response?.success === true;
				const message = response?.message || (isSuccess ? 'Email sent successfully' : 'Failed to send email');
				const severity = isSuccess ? 'success' : 'error';
				
				this.clearAndAddToast(
					severity,
					severity === 'success' 
						? this.translateService.instant('common.success')
						: this.translateService.instant('common.error'),
					message,
					3000 // Auto-dismiss after 3 seconds
				);
			},
			error: (error: any) => {
				this.isDownloading = false;
				this.showEmailDialog = false;
				this.pendingInvoiceId = null;
				
				const errorMessage = error?.error?.message || error?.message || 'Failed to send email';
				this.clearAndAddToast(
					'error',
					this.translateService.instant('common.error'),
					errorMessage,
					3000 // Auto-dismiss after 3 seconds
				);
			}
		});
	}

	/**
	 * Update customer email and then send invoice (for permanent email)
	 */
	private updateCustomerEmailAndSendInvoice(customerId: number, email: string, invoiceId: number): void {
		// First, get current customer data
		this.httpHelpService.getCustomerById(customerId).subscribe({
			next: (customerData: any) => {
				// Prepare update payload with all customer fields
				const updatePayload = {
					customer_Building_number: customerData.customer_Building_number || " ",
					customer_address_city: customerData.customer_address_city || " ",
					customer_address_str: customerData.customer_address_str || " ",
					customer_address_zip_code: customerData.customer_address_zip_code || " ",
					customer_email: email,
					customer_name_ar: customerData.customer_name_ar || "",
					customer_name_en: customerData.customer_name_en || "",
					customer_name_he: customerData.customer_name_he || "",
					customer_phone: customerData.customer_phone || "",
					customer_type: customerData.customer_type || 6,
					customer_vat_number: customerData.customer_vat_number || "",
					customer_vat_tax: customerData.customer_vat_tax || "",
					id_number: customerData.id_number || "",
					updated_by: JSON.parse(localStorage.getItem('user_id') || 'null')
				};

				// Update customer
				this.httpHelpService.updateCustomersById(customerId, updatePayload).subscribe({
					next: (updateResponse: any) => {
						// After updating customer, send invoice WITHOUT tempEmail
						this.sendInvoiceAfterEmailUpdate(invoiceId);
					},
					error: (updateError: any) => {
						this.showEmailDialog = false;
						const errorMessage = updateError?.error?.message || updateError?.message || 'Failed to update customer email';
						this.clearAndAddToast(
							'error',
							this.translateService.instant('common.error'),
							errorMessage,
							3000 // Auto-dismiss after 3 seconds
						);
					}
				});
			},
			error: (error: any) => {
				this.showEmailDialog = false;
				const errorMessage = error?.error?.message || error?.message || 'Failed to load customer data';
				this.clearAndAddToast(
					'error',
					this.translateService.instant('common.error'),
					errorMessage,
					3000 // Auto-dismiss after 3 seconds
				);
			}
		});
	}

	/**
	 * Send invoice after email has been updated (permanent email - no tempEmail in payload)
	 */
	private sendInvoiceAfterEmailUpdate(invoiceId: number): void {
		this.isDownloading = true;
		
		// Send invoice WITHOUT tempEmail (customer already has email saved)
		this.httpHelpService.sendInvoiceToCustomerEmail(invoiceId).subscribe({
			next: (response: any) => {
				this.isDownloading = false;
				this.showEmailDialog = false;
				this.pendingInvoiceId = null;
				
				const isSuccess = response?.success === true;
				const message = response?.message || (isSuccess ? 'Email sent successfully' : 'Failed to send email');
				const severity = isSuccess ? 'success' : 'error';
				
				this.clearAndAddToast(
					severity,
					severity === 'success' 
						? this.translateService.instant('common.success')
						: this.translateService.instant('common.error'),
					message,
					3000 // Auto-dismiss after 3 seconds
				);
			},
			error: (error: any) => {
				this.isDownloading = false;
				this.showEmailDialog = false;
				this.pendingInvoiceId = null;
				
				const errorMessage = error?.error?.message || error?.message || 'Failed to send email';
				this.clearAndAddToast(
					'error',
					this.translateService.instant('common.error'),
					errorMessage,
					3000 // Auto-dismiss after 3 seconds
				);
			}
		});
	}

	/**
	 * Cancel email dialog
	 */
	onCancelEmailDialog(): void {
		this.showEmailDialog = false;
		this.pendingInvoiceId = null;
		this.emailForm.reset({
			email: '',
			savePermanently: false
		});
	}

	/**
	 * Get the title for the modal, including document number if available
	 */
	getModalTitle(): string {
		if (this.documentNumber) {
			const key = 'allDocuments.modal.titleWithNumber';
			const hasKey = this.translateKeyLocal(key) !== key;

			if (hasKey) {
				return this.translateKeyLocal(key, { number: this.documentNumber });
			} else {
				const title = this.translateKeyLocal('allDocuments.modal.title');
				return `${title} - ${this.documentNumber}`;
			}
		}
		return this.translateKeyLocal('allDocuments.modal.title');
	}

	/**
	 * Local translation method to avoid mixed language display
	 */
	translateKeyLocal(key: string, params?: Record<string, any>): string {
		try {
			// helper to pick nested value from the translations object
			const getNested = (obj: any, path: string) => {
				if (!obj) return undefined;
				return path.split('.').reduce((acc: any, part: string) => (acc && acc[part] !== undefined) ? acc[part] : undefined, obj);
			};

			let value: any = undefined;
			if (this.localTranslations) {
				value = getNested(this.localTranslations, key);
			}
			
			// If local translations are not available yet, fall back to global translate service
			// This ensures translations are always shown, even during initialization
			if (value === undefined) {
				// Fall back to global translate service to ensure translations are shown
				return this.translateService.instant(key, params);
			}
			
			if (!value) return this.translateService.instant(key, params);

			// simple interpolation for params using {{key}} or {{ key }} syntax
			if (params && typeof value === 'string') {
				Object.keys(params).forEach(p => {
					const re = new RegExp('{{\\s*' + p + '\\s*}}', 'g');
					value = value.replace(re, String(params[p]));
				});
			}
			return value;
		} catch (e) {
			// Fall back to global translate service to ensure translations are shown
			return this.translateService.instant(key, params);
		}
	}

	/**
	 * Global translation method for modal UI elements that should stay in site language
	 */
	translateGlobal(key: string, params?: Record<string, any>): string {
		try {
			return this.translateService.instant(key, params);
		} catch (e) {
			return key;
		}
	}

	/**
	 * Load local translations for the modal to avoid mixed language display
	 */
	private loadModalTranslations(language: string): void {
		try {
			// Try to get translations synchronously first
			const currentTranslations = this.translateService.translations[language];
			if (currentTranslations) {
				this.localTranslations = currentTranslations;
			}
			
			// Load translations WITHOUT changing global language
			this.translateService.getTranslation(language).subscribe(trans => {
				this.localTranslations = trans || {};
			}, err => {
				console.warn('[Modal] Failed to load translations for', language, err);
				// Don't set to null, keep existing translations
			});
		} catch (e) {
			console.warn('[Modal] Error loading translations for', language, e);
			// Don't set to null, keep existing translations
		}
	}

	openDialog(data: DocumentData | SimpleDocumentData): void {
		if ('documentId' in data && typeof data.documentId !== 'undefined') {
			this.openDialogSimple(data as SimpleDocumentData);
		} else {
			this.openDialogLegacy(data as DocumentData);
		}
	}

	private openDialogSimple(data: SimpleDocumentData): void {
	this.documentId = String(data.documentId);
	this.documentNumber = data.documentNumber || "";
	// Use invoice language if set, otherwise default to Hebrew
	this.originalDocumentLanguage = data.documentLanguage || "he";
	this.currentDocumentData = data.documentData || null;
	this.pdfLoading = true;
	
	// Set language IMMEDIATELY to ensure it's available before template rendering
	this.selectedLanguage = this.originalDocumentLanguage;
	
	// Load modal translations for the selected language
	this.loadModalTranslations(this.selectedLanguage);

	this.PdfLink = '';
	this.rawPdfUrl = '';
	this.documentHtmlContent = '';
	this.pdfUrl = null;

	// Initialize share menu items BEFORE showing dialog
	this.initShareMenuItems();
	
	// Prevent language change handler from triggering during initialization
	this._isInitializing = true;
	
	// Hide language selector during initialization to prevent any onChange events
	this.showLanguageSelector = false;
	
	// If auto-trigger email share is requested, show ONLY email dialog (not PDF modal)
	if ((data as any).autoTriggerEmailShare && this.documentId) {
		const invoiceId = Number(this.documentId);
		if (!isNaN(invoiceId)) {
			// Set pending invoice ID and show email dialog immediately
			// Do NOT show the PDF modal dialog
			this.pendingInvoiceId = invoiceId;
			this.showEmailDialog = true;
			this.emailForm.reset({
				email: '',
				savePermanently: false
			});
			// Store document data for later use if needed (for customer ID)
			if (data.documentData) {
				this.currentDocumentData = data.documentData;
			} else {
				// If document data is not provided, fetch it to get customer ID
				// This is needed for updating customer email if user checks "save permanently"
				this.httpHelpService.getInvoicesAndReceiptsByInvoiceid(invoiceId).subscribe({
					next: (res) => {
						this.currentDocumentData = res;
					},
					error: (err) => {
						console.warn('Could not fetch document data for email dialog:', err);
						// Continue anyway - customer ID might be available from other sources
					}
				});
			}
			return; // Exit early, don't show PDF modal
		}
	}
	
	// Show dialog AFTER everything is set up (only if not auto-triggering email)
	this.displayDialog = true;

		if (this.documentId) {
			this.httpHelpService.getInvoicesAndReceiptsByInvoiceid(Number(this.documentId)).subscribe(
				(res) => {
					this.currentDocumentData = res;
					// Set allocation/procedure flag based on the server response
					this.hasAllocationDenial = !!res && !!res.is_in_procedure_cycle;

					// Preserve PDF URLs if they exist in the response, otherwise clear them
					this.PdfLink = res?.pdfLink || res?.pdf_link || '';
					this.rawPdfUrl = res?.rawPdfUrl || res?.raw_pdf_url || '';
					this.pdfUrl = res?.pdfUrl || res?.pdf_url || null;
					this.pdfLoading = false;

					this.documentHtmlContent = '';

					// Enhance invoice data with company type information
					try {
						const enhancementPromise = this.enhanceInvoiceDataWithCompanyType(res);
						enhancementPromise.then(enhancedRes => {
							setTimeout(() => {
								// Only render if we haven't already rendered (prevent double rendering)
								if (this._isInitializing) {
									// Always use the invoice's original language for rendering invoice content
									this.renderTemplateComponent(enhancedRes, this.originalDocumentLanguage);
									
									// Reset initialization flag after first render completes
									setTimeout(() => {
										this._isInitializing = false;
										// Show language selector after the invoice is fully loaded and stable
										this.showLanguageSelector = true;
									}, 200); // Reduced delay since flash is fixed
								}
							}, 100);
						}).catch(error => {
							console.error('🔍 [DEBUG] Error in enhanceInvoiceDataWithCompanyType:', error);
						});
					} catch (error) {
						console.error('🔍 [DEBUG] Error creating enhancement promise:', error);
					}
				},
				(err) => {
					console.error("Error loading document details:", err);
					this.pdfLoading = false;
					this.PdfLink = '';
					this.rawPdfUrl = '';
					this.pdfUrl = null;
					this.documentHtmlContent = '<div class="error-message"><p>خطأ في تحميل تفاصيل المستند</p></div>';
				}
			);
		}
	}

	private openDialogLegacy(data: DocumentData): void {
		this.PdfLink = data.pdfLink;
		this.rawPdfUrl = data.rawPdfUrl;
		this.pdfUrl = data.pdfUrl;
		this.documentNumber = data.documentNumber || '';
		this.documentHtmlContent = data.documentHtmlContent || '';
		this.documentId = data.documentId || '';
		this.originalDocumentLanguage = data.documentLanguage || 'en';

		this.pdfLoading = true;
		this.selectedLanguage = this.curentLang;

		this.initShareMenuItems();
		// Ensure the language selector reflects the app's active language
		this.selectedLanguage = (this.translateService.currentLang as string) || this.curentLang || localStorage.getItem('curentLang') || 'en';
		this.displayDialog = true;
	}

	closeDialog(): void {
		this.displayDialog = false;
		this.closeShareMenu();
		this.pdfLoading = false;
		this.isChangingDocumentLanguage = false;
		
		this.currentDocumentData = null;
		
		this.cleanupTemplateComponent();
		
		this.maximizedOnce = false;
	}

	onPdfLoaded(): void {
		this.pdfLoading = false;
		this.pdfLoaded.emit();
	}

	downloadDocumentPdf(): void {
		if (this.documentId && this.isChangingDocumentLanguage) {
			this.clearAndAddToast(
				'info',
				this.translateService.instant('common.pleaseWait'),
				this.translateService.instant('common.documentLoading')
			);
			return;
		}

		if (this.documentId) {
			this.isDownloading = true;

			this.clearAndAddToast(
				'info',
				this.translateService.instant('allDocuments.modal.preparingFile'),
				this.translateService.instant('common.pleaseWait')
			);

			this.httpHelpService.downloadDocumentPdf(Number(this.documentId), this.originalDocumentLanguage).subscribe({
					next: (blob: any) => {
					const url = window.URL.createObjectURL(blob);
					const link = document.createElement('a');
					link.href = url;
					const filename = this.documentNumber
						? `Document-${this.documentNumber}-${this.originalDocumentLanguage}.pdf`
						: `Document-${this.originalDocumentLanguage}.pdf`;
					link.download = filename;
					link.click();
					window.URL.revokeObjectURL(url);

					this.clearAndAddToast(
						'success',
						this.translateService.instant('common.success'),
						this.translateService.instant('common.downloadSuccess')
					);

					this.isDownloading = false;
				},
					error: (error: any) => {
					console.error('Error downloading document:', error);
					this.clearAndAddToast(
						'error',
						this.translateService.instant('common.error'),
						this.translateService.instant('common.downloadError')
					);

					this.isDownloading = false;
				}
			});
		} else if (this.rawPdfUrl) {
			this.downloadFromUrl();
		}
	}

	/**
	 * Fallback method to download from URL
	 */
	private downloadFromUrl(): void {
		this.isDownloading = true;

		this.clearAndAddToast(
			'info',
			this.translateService.instant('allDocuments.modal.preparingFile'),
			this.translateService.instant('common.pleaseWait')
		);

		fetch(this.rawPdfUrl)
			.then(response => response.blob())
			.then(blob => {
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				const filename = this.documentNumber
					? `Document-${this.documentNumber}-${this.originalDocumentLanguage}.pdf`
					: `Document-${this.originalDocumentLanguage}.pdf`;
				link.download = filename;
				link.click();
				window.URL.revokeObjectURL(url);

				this.clearAndAddToast(
					'success',
					this.translateService.instant('common.success'),
					this.translateService.instant('common.downloadSuccess')
				);

				this.isDownloading = false;
			})
			.catch(error => {
				console.error('Error downloading document:', error);
				this.clearAndAddToast(
					'error',
					this.translateService.instant('common.error'),
					this.translateService.instant('common.downloadError')
				);

				this.isDownloading = false;
			});
	}

	downloadOriginalDocPdfFromLink(): void {
		this.downloadDocumentPdf();
	}

	copyLinkToClipboard(url: string): void {
		if (navigator.clipboard) {
			navigator.clipboard.writeText(url)
				.then(() => {
					this.clearAndAddToast(
						'success',
						this.translateService.instant('common.success'),
						this.translateService.instant('common.linkCopied')
					);
				})
				.catch(err => {
					console.error('Could not copy text: ', err);
					this.clearAndAddToast(
						'error',
						this.translateService.instant('common.error'),
						this.translateService.instant('common.copyError')
					);
				});
		} else {
			const textArea = document.createElement('textarea');
			textArea.value = url;
			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();
			try {
				document.execCommand('copy');
				this.clearAndAddToast(
					'success',
					this.translateService.instant('common.success'),
					this.translateService.instant('common.linkCopied')
				);
			} catch (err) {
				console.error('Could not copy text: ', err);
				this.clearAndAddToast(
					'error',
					this.translateService.instant('common.error'),
					this.translateService.instant('common.copyError')
				);
			}
			document.body.removeChild(textArea);
		}
	}

	/**
	 * Handle language change from the language switcher (wrapper to prevent initialization calls)
	 */
	handleLanguageChange(event: any): void {
		// Prevent language change during initialization to avoid double rendering
		if (this._isInitializing) {
			return;
		}
		this.onLanguageChange(event);
	}

	/**
	 * Handle language change from the language switcher
	 */
	onLanguageChange(event: any): void {
		const newLanguage = event.value;
		this.originalDocumentLanguage = newLanguage;

		// Load modal translations for the new language
		this.loadModalTranslations(newLanguage);

		if (this.templateContainer && this.currentDocumentData) {
			this.isChangingDocumentLanguage = true;
			this.pdfLoading = true;

			setTimeout(() => {
				this.renderTemplateComponent(this.currentDocumentData, newLanguage);
				this.isChangingDocumentLanguage = false;
				this.pdfLoading = false;
			}, 300);
		}

		this.languageChanged.emit(newLanguage);
	}

	/**
	 * Enhance invoice data with company type information
	 */
	private async enhanceInvoiceDataWithCompanyType(invoiceData: any): Promise<any> {
		if (!invoiceData?.company?.company_id) {
			return invoiceData;
		}

		try {
			// Fetch all companies to get companyType data (getCompanyInfoById doesn't include companyType)
			const allCompanies = await this.httpHelpService.getAllActiveCompanyByMerchant().toPromise();
			// Find the specific company by ID
			const targetCompany = allCompanies.find((company: any) => company.company_id === invoiceData.company.company_id);
			// Add companyType to the invoice data
			if (targetCompany && targetCompany.companyType) {
				invoiceData.company.companyType = targetCompany.companyType;
				} else {
				}
			
			return invoiceData;
		} catch (error) {
			console.error('🔍 [DEBUG] Failed to fetch company type information:', error);
			return invoiceData; // Return original data if enhancement fails
		}
	}

	/**
	 * Load document data with specific language (for future backend support)
	 */
	private loadDocumentWithLanguage(language: string): void {
		if (!this.documentId) return;

		this.httpHelpService.getInvoicesAndReceiptsByInvoiceid(Number(this.documentId)).subscribe(
			(res) => {
				this.currentDocumentData = res;
				
				this.isChangingDocumentLanguage = false;
				this.pdfLoading = false;
				
				// Enhance invoice data with company type information
				this.enhanceInvoiceDataWithCompanyType(res).then(enhancedRes => {
					setTimeout(() => {
						this.renderTemplateComponent(enhancedRes, language);
					}, 100);
				});
			},
			(err) => {
				console.error("Error loading document with language:", err);
				this.isChangingDocumentLanguage = false;
				this.pdfLoading = false;
				
				if (this.currentDocumentData) {
					this.renderTemplateComponent(this.currentDocumentData, language);
				}
			}
		);
	}

	/**
	 * Get language name by code
	 */
	getLanguageName(code: string): string {
		const language = this.availableLanguages.find(lang => lang.code === code);
		return language ? language.name : code.toUpperCase();
	}

	/**
	 * Get current language name for dropdown placeholder
	 */
	getCurrentLanguageName(): string {
		return this.getLanguageName(this.selectedLanguage);
	}

	/**
	 * Get the PDF URL for sharing (cloud URL or constructed URL)
	 */
	getPdfUrlForSharing(): string {
		// First try cloud URLs
		if (this.rawPdfUrl) {
			return this.rawPdfUrl;
		}
		if (this.PdfLink) {
			return this.PdfLink;
		}
		// Fallback to constructed URL
		if (this.documentId) {
			return `${baseUrl}export-pdf/Invoice-pdf/${this.documentId}/${this.originalDocumentLanguage}`;
		}
		return '';
	}

	/**
	 * Get responsive dropdown style based on screen size
	 */
	getDropdownStyle(): any {
		const isMobile = this.isMobileDevice();
		const screenWidth = window.innerWidth;
		
		if (isMobile) {
			// Very small screens (phones in portrait)
			if (screenWidth <= 480) {
				return { 
					minWidth: '100px', 
					fontSize: '0.7rem',
					maxWidth: '120px'
				};
			}
			// Small mobile screens
			else if (screenWidth <= 640) {
				return { 
					minWidth: '110px', 
					fontSize: '0.75rem',
					maxWidth: '130px'
				};
			}
			// Regular mobile screens
			else {
				return { 
					minWidth: '120px', 
					fontSize: '0.8rem',
					maxWidth: '140px'
				};
			}
		}
		
		// Desktop screens
		return { 
			minWidth: '140px',
			fontSize: '0.85rem'
		};
	}

	/**
	 * Generate PDF and share it via the specified method
	 */
	private generateAndSharePdf(shareMethod: 'copy' | 'whatsapp' | 'email'): void {
		this.clearAndAddToast(
			'info',
			this.translateService.instant('common.info'),
			this.translateService.instant('common.pleaseWait')
		);

		this.httpHelpService.downloadDocumentPdf(Number(this.documentId), this.originalDocumentLanguage).subscribe({
			next: (blob) => {
				const url = window.URL.createObjectURL(blob);
				
				switch (shareMethod) {
					case 'copy':
						this.copyPdfBlobToClipboard(blob);
						break;
					case 'whatsapp':
						this.sharePdfViaWhatsApp(url);
						break;
					case 'email':
						this.sharePdfViaEmail(url);
						break;
				}
				
				// Clean up the URL after a delay to allow sharing to complete
				setTimeout(() => {
					window.URL.revokeObjectURL(url);
				}, 10000);
			},
			error: (error) => {
				console.error('Error generating PDF for sharing:', error);
				this.clearAndAddToast(
					'error',
					this.translateService.instant('common.error'),
					'Failed to generate PDF for sharing. Please try again.'
				);
			}
		});
	}

	/**
	 * Copy PDF blob to clipboard
	 */
	private copyPdfBlobToClipboard(blob: Blob): void {
		const filename = this.documentNumber
			? `Document-${this.documentNumber}-${this.originalDocumentLanguage}.pdf`
			: `Document-${this.originalDocumentLanguage}.pdf`;

		// Create a file object from the blob
		const file = new File([blob], filename, { type: 'application/pdf' });
		
		// Use the Clipboard API to copy the file
		if (navigator.clipboard && navigator.clipboard.write) {
			const clipboardItem = new ClipboardItem({
				'application/pdf': blob
			});
			
			navigator.clipboard.write([clipboardItem]).then(() => {
				this.clearAndAddToast(
					'success',
					this.translateService.instant('common.success'),
					'PDF copied to clipboard'
				);
			}).catch(err => {
				console.error('Could not copy PDF to clipboard:', err);
				this.fallbackCopyPdf(blob, filename);
			});
		} else {
			this.fallbackCopyPdf(blob, filename);
		}
	}

	/**
	 * Fallback method to copy PDF when Clipboard API is not available
	 */
	private fallbackCopyPdf(blob: Blob, filename: string): void {
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename;
		link.click();
		window.URL.revokeObjectURL(url);
		
		this.clearAndAddToast(
			'success',
			this.translateService.instant('common.success'),
			'PDF downloaded - you can now share the file'
		);
	}

	/**
	 * Share PDF via WhatsApp
	 */
	private sharePdfViaWhatsApp(pdfUrl: string): void {
		const message = this.documentNumber 
			? `Invoice ${this.documentNumber} - ${this.getCurrentLanguageName()}`
			: `Document - ${this.getCurrentLanguageName()}`;
		
		window.open(`https://wa.me/?text=${encodeURIComponent(message + ' ' + pdfUrl)}`, '_blank');
		
		this.clearAndAddToast(
			'success',
			this.translateService.instant('common.success'),
			'Opening WhatsApp to share PDF'
		);
	}

	/**
	 * Share PDF via Email
	 */
	private sharePdfViaEmail(pdfUrl: string): void {
		const subject = this.documentNumber 
			? `Invoice ${this.documentNumber} - ${this.getCurrentLanguageName()}`
			: `Document - ${this.getCurrentLanguageName()}`;
		
		const body = `Please find attached the ${this.getCurrentLanguageName()} version of the document.`;
		
		window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body + ' ' + pdfUrl)}`, '_blank');
		
		this.clearAndAddToast(
			'success',
			this.translateService.instant('common.success'),
			'Opening email client to share PDF'
		);
	}

  /**
   * Print the document using the browser's print dialog
   * Now supports language-specific PDF generation
   */
	printDocument(): void {
		try {
			if (this.documentId) {
				this.printDocumentWithLanguage();
				return;
			}

			if (!this.pdfIframe || this.pdfLoading) {
				this.clearAndAddToast(
					'info',
					this.translateService.instant('common.info'),
					this.translateService.instant('common.documentLoading')
				);
				return;
			}

			this.clearAndAddToast(
				'info',
				this.translateService.instant('common.info'),
				this.translateService.instant('common.pleaseWait')
			);

			const iframe = this.pdfIframe.nativeElement;

			if (iframe.contentWindow) {
				setTimeout(() => {
					try {
						iframe.contentWindow.focus();
						iframe.contentWindow.print();

						this.clearAndAddToast(
							'success',
							this.translateService.instant('common.success'),
							this.translateService.instant('common.printSuccess')
						);
					} catch (iframeError) {
						console.error('Error printing from iframe:', iframeError);
						this.printInNewWindow();
					}
				}, 500);
			} else {
				this.printInNewWindow();
			}
		} catch (error) {
			console.error('Error in printDocument:', error);
			this.printInNewWindow();
		}
	}

	/**
	 * Print document with language-specific PDF generation
	 */
	private printDocumentWithLanguage(): void {
		this.clearAndAddToast(
			'info',
			this.translateService.instant('common.info'),
			this.translateService.instant('common.pleaseWait')
		);

		this.httpHelpService.downloadDocumentPdf(Number(this.documentId), this.originalDocumentLanguage).subscribe({
			next: (blob) => {
				// Use iframe printing to avoid popup blocker
				this.printBlobInIframe(blob);
			},
			error: (error) => {
				console.error('Error generating PDF for print:', error);
				this.clearAndAddToast(
					'error',
					this.translateService.instant('common.error'),
					'Failed to generate PDF for printing. Please try downloading and printing manually.'
				);
			}
		});
	}

	/**
	 * Fallback print method that opens PDF in new tab without popup blocker
	 * Used if direct iframe printing fails
	 */
	private printInNewWindow(): void {
		try {
			if (!this.rawPdfUrl) {
				this.clearAndAddToast(
					'error',
					this.translateService.instant('common.error'),
					'PDF URL is not available'
				);
				return;
			}

			// Open PDF in new tab without popup blocker
			this.openPdfInNewTab(this.rawPdfUrl);
		} catch (error) {
			console.error('Error opening document for print in new window:', error);
			this.clearAndAddToast(
				'error',
				this.translateService.instant('common.error'),
				'Failed to open document. Please try downloading and printing manually.'
			);
		}
	}

	/**
	 * Print a PDF blob by opening it in a new tab without popup blocker
	 */
	private printBlobInIframe(blob: Blob): void {
		const url = window.URL.createObjectURL(blob);
		this.openPdfInNewTab(url);
		
		// Clean up the blob URL after a delay
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
		}, 10000);
	}

	/**
	 * Open PDF in new tab without popup blocker by using user interaction context
	 */
	private openPdfInNewTab(url: string): void {
		// Create a temporary link element to trigger download/view in new tab
		const link = document.createElement('a');
		link.href = url;
		link.target = '_blank';
		link.rel = 'noopener noreferrer';
		
		// Add to DOM temporarily
		link.style.display = 'none';
		document.body.appendChild(link);
		
		// Trigger click programmatically (this bypasses popup blocker)
		link.click();
		
		// Clean up
		document.body.removeChild(link);
		
		this.clearAndAddToast(
			'success',
			this.translateService.instant('common.success'),
			'PDF opened in new tab - you can now print from there',
			3000 // Auto-dismiss after 3 seconds
		);
	}

	/**
	 * Check if the current device is a mobile device
	 * @returns boolean indicating if the device is mobile
	 */
	isMobileDevice(): boolean {
		return window.innerWidth <= 768;
	}

	/**
	 * Opens allocation denial actions
	 */
	openAllocationDenialActions(): void {
		if (this.currentDocumentData) {
			this.allocationDenialActionsRequested.emit(this.currentDocumentData);
		}
	}

	/**
	 * Generate invoice HTML using the dynamic template service (async)
	 */
	private async generateInvoiceHTML(invoiceData: any): Promise<string> {
		const currentLang = this.translateService.currentLang as 'en' | 'ar' | 'he' || 'he';
		
		try {
			if (!this.templateContainer) {
				console.warn('Template container not ready, using fallback HTML generation');
				return this.generateFallbackHTML(invoiceData);
			}

			const html = await this.invoiceTemplateService.generateInvoiceHTML(
				invoiceData,
				currentLang,
				this.templateContainer
			);
			return html;
		} catch (error) {
			console.error('❌ Error generating template HTML:', error);
			return this.generateFallbackHTML(invoiceData);
		}
	}

	/**
	 * Render template component for interactive display
	 */
	private renderTemplateComponent(invoiceData: any, language?: string): void {
		if (!this.templateContainer) {
			console.warn('Template container not ready for rendering');
			return;
		}

		// Debug company type information
		// Log container element dimensions
		const containerElement = this.templateContainer.element.nativeElement as HTMLElement;
		const dialogContent = document.querySelector('.p-dialog-content') as HTMLElement;
		const templateHostInner = document.querySelector('.template-host-inner') as HTMLElement;
		
		// Prevent double rendering during initialization
		if (this._isInitializing && this.documentHtmlContent) {
			return;
		}

		try {
			const currentLang = (language || this.originalDocumentLanguage) as 'en' | 'ar' | 'he';
			const container = this.templateContainer; // capture for closures
			if (!container) {
				console.warn('Template container not ready for rendering (captured)');
				return;
			}
			
			// Cleanup OLD content first, but BEFORE we get the host element reference
			this.cleanupTemplateComponent();
			// Instead of rendering the invoice component into the host (where global styles
			// leak in), we create an isolated iframe and write the generated HTML into it.
			// This guarantees the invoice visuals exactly match the desktop template.
			try {
				// generateInvoiceHTML is async to allow translation preloading — handle it as a promise
				const htmlPromise = this.invoiceTemplateService.generateInvoiceHTML(invoiceData, currentLang, container) as Promise<string>;
				htmlPromise.then((html) => {

				// Create iframe and inject
				const iframe = document.createElement('iframe');
				iframe.setAttribute('sandbox', 'allow-same-origin allow-scripts allow-popups');
				iframe.style.width = '100%';
				iframe.style.border = '0';
				iframe.style.display = 'block';
				// Start with auto height so it doesn't artificially inflate measurements
				iframe.style.height = 'auto';
				iframe.style.minHeight = '100px';
				// Hide iframe initially
				iframe.style.visibility = 'hidden';
				iframe.id = 'injected-invoice-iframe-' + Date.now();

				// Get the host element and append iframe (must be in DOM for contentDocument to work)
				const hostElem = container.element.nativeElement as HTMLElement;
				
				// Append iframe to DOM so contentDocument becomes available
				hostElem.appendChild(iframe);
				this._injectedInvoiceIframe = iframe;

					// Write content into the iframe document
					const idoc = iframe.contentDocument || iframe.contentWindow?.document;
					if (idoc) {
					idoc.open();
					// Before writing the HTML, copy relevant stylesheet links so fonts and global rules load
					try {
						const parentLinks = Array.from(document.head.querySelectorAll('link[rel="stylesheet"]')) as HTMLLinkElement[];
						parentLinks.forEach(l => {
							try {
								const nl = idoc.createElement('link');
								nl.rel = 'stylesheet';
								nl.href = l.href;
								idoc.head.appendChild(nl);
							} catch (e) { /* ignore cross-origin/link creation errors */ }
						});
					} catch (e) { /* ignore */ }

					// Use document.write() - it's the most reliable method for iframe content
					idoc.write(html);
					idoc.close();

					// After writing HTML, copy any CSS rules that look relevant (component styles, invoice selectors)
					try {
						let cssToCopy = '';
						const keywords = ['.page', '.payment-table', '.totals', '.invoice', '.toolbar', '[_nghost', '[_ngcontent'];
						for (let i = 0; i < document.styleSheets.length; i++) {
							const sheet = document.styleSheets[i] as CSSStyleSheet;
							try {
								const rules = sheet.cssRules || [];
								for (let j = 0; j < rules.length; j++) {
									const rule = rules[j] as CSSStyleRule;
									if (!rule || !rule.cssText) continue;
									const text = rule.cssText;
									for (let k = 0; k < keywords.length; k++) {
										if (text.indexOf(keywords[k]) !== -1) {
											cssToCopy += text + '\n';
											break;
										}
									}
								}
							} catch (err) {
								// ignore CORS-protected sheets
								continue;
							}
						}
						if (cssToCopy) {
							const s = idoc.createElement('style');
							s.id = 'copied-invoice-styles';
							s.appendChild(idoc.createTextNode(cssToCopy));
							idoc.head.appendChild(s);
						}
					} catch (e) { /* ignore */ }
						// Ensure the iframe document has sane defaults (no margins, white background)
						try {
							idoc.documentElement.style.background = '#ffffff';
							idoc.body.style.margin = '0';
							idoc.body.style.padding = '0';
							idoc.body.style.background = '#ffffff';
							idoc.body.style.color = '#1a1a1a';
							idoc.body.style.minHeight = 'fit-content';

							// Set document direction to match requested language
							try {
								const isRtl = (currentLang === 'ar' || currentLang === 'he');
								idoc.documentElement.setAttribute('dir', isRtl ? 'rtl' : 'ltr');
							} catch (e) { /* ignore */ }
							// Add a small forcing stylesheet to make .page visible and reset positioning
							const resetStyle = idoc.createElement('style');
							resetStyle.id = 'invoice-iframe-reset';
							resetStyle.appendChild(idoc.createTextNode(`
								html, body { background: #ffffff !important; color: #1a1a1a !important; margin: 0 !important; padding: 0 !important; min-height: fit-content !important; font-family: 'Tajawal', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial !important; }
								.page { max-width: 1024px !important; margin: 18px auto !important; padding: 0 12px !important; direction: rtl !important; display: block !important; position: static !important; opacity: 1 !important; transform: none !important; left: auto !important; box-sizing: border-box !important; }
								.page * { visibility: visible !important; }
							`));
							idoc.head.appendChild(resetStyle);
						} catch (e) { /* ignore */ }

					// Resize iframe to fit content and make visible
				setTimeout(() => {
					try {
						// Calculate the proper height now that content has rendered
						const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
						if (iframeDoc && iframeDoc.body) {
							// Check for .page element inside iframe
							const pageInIframe = iframeDoc.querySelector('.page') as HTMLElement;
// Check for .invoice element inside iframe
							const invoiceInIframe = iframeDoc.querySelector('.invoice') as HTMLElement;
// Use body scrollHeight as the primary measurement since it reflects actual content
							// Don't use documentElement.scrollHeight as it can be inflated by the iframe's initial height
							const contentHeight = Math.max(
								iframeDoc.body.scrollHeight,
								iframeDoc.body.offsetHeight
							);
							
							if (contentHeight > 100) {
								// Resize iframe to fit content exactly
								iframe.style.height = contentHeight + 'px';
								}
							
							}
						
						// Wait for iframe content to fully render before showing
						setTimeout(() => {
							iframe.style.visibility = 'visible';
							this.pdfLoading = false;
							}, 100);
						
						try { 
							this.applyInvoiceInlineStyles(); 
						} catch (e) { 
							console.warn('[InvoiceDiag] applyInvoiceInlineStyles failed for iframe', e); 
						}
					} catch (err) {
						console.error('Error in iframe setup:', err);
					}
				}, 200);
				} // End of if (idoc)
					}).catch(err => {
					console.warn('[AlldocumentsViewPdfModal] generateInvoiceHTML failed', err);
					// fallback to original render path
					if (container) {
						this.currentTemplateComponent = this.invoiceTemplateService.renderTemplateComponent(
							invoiceData,
							currentLang,
							container
						);
					} else {
						console.error('[AlldocumentsViewPdfModal] cannot fallback to renderTemplateComponent: container null');
					}
				});
			} catch (err) {
				console.warn('[AlldocumentsViewPdfModal] iframe injection failed, falling back to direct component render', err);
				// fallback to original dynamic component rendering
				this.currentTemplateComponent = this.invoiceTemplateService.renderTemplateComponent(
					invoiceData,
					currentLang,
					this.templateContainer
				);
			}

			// Diagnostics disabled to prevent interference with iframe display
			// setTimeout(() => {
			// 	try {
			// 		this.logInvoiceStyleDiagnostics();
			// 	} catch (e) {
			// 		console.warn('[InvoiceDiag] diagnostics failed to run', e);
			// 	}
			// }, 250);

			// Style overrides disabled to prevent interference with iframe display
			// setTimeout(() => {
			// 	try {
			// 		this.applyInvoiceInlineStyles();
			// 	} catch (e) {
			// 		console.warn('[InvoiceDiag] applyInvoiceInlineStyles failed', e);
			// 	}
			// }, 300);

			// --- Diagnostics: log layout info to help debug centering issues ---
			try {
				// diagnostics removed: layout debug logging and visual guide cleaned up

				// No dialog repositioning -- centering is handled via CSS on the template child only
			} catch (err) {
				console.error('Failed to schedule layout debug logging:', err);
			}
			// --- End diagnostics ---

			// Ensure any leftover inline positioning from previous runs is cleared so CSS can center the dialog
			// This only removes inline positioning attributes (left/transform/width) if present — it does not
			// modify computed styles or classes. We run it after a short delay to allow the dialog to finish
			// PrimeNG/animation setup.
			setTimeout(() => {
				this.clearInlineDialogStyles();
			}, 200);

				// Conservative repositioning: compute centered left and apply inline style to dialog
				// Disabled: temporarily commenting out JS nudging so CSS flex centering can control layout.
				// This was moving the dialog itself and applying transforms to the template child which
				// interfered with letting the injected HTML take its natural width. If you need this
				// behavior later, uncomment the block below.
				/*
				try {
					setTimeout(() => {
						const dialogRoot = document.querySelector('.pdf-viewer-dialog') as HTMLElement | null;
						if (!dialogRoot) return;
						const applyCenter = () => {
							const winW = window.innerWidth || document.documentElement.clientWidth;
							const desiredLeft = Math.max(0, Math.round((winW - dialogRoot.offsetWidth) / 2));
							if (Math.abs(dialogRoot.offsetLeft - desiredLeft) > 2) {
								dialogRoot.style.left = desiredLeft + 'px';
								dialogRoot.style.transform = '';
								return true;
							}
							return false;
						};
						applyCenter();
						[200, 450, 900].forEach(delay => setTimeout(applyCenter, delay));
						setTimeout(() => {
							// content centering logic (commented to avoid moving dialog)
						}, 1400);
						const reposition = () => {};
						if (this._dialogRepositionListener) {
							window.removeEventListener('resize', this._dialogRepositionListener as any);
						}
						this._dialogRepositionListener = reposition as any;
						window.addEventListener('resize', this._dialogRepositionListener as any);
					}, 160);
				} catch (e) {
					// ignore errors
				}
				*/
		} catch (error) {
			console.error('❌ Error rendering template component:', error);
			this.documentHtmlContent = this.generateFallbackHTML(invoiceData);
		}
	}

	/**
	 * Cleanup the current template component
	 */
	private cleanupTemplateComponent(): void {
		if (this.currentTemplateComponent) {
			this.currentTemplateComponent.destroy();
			this.currentTemplateComponent = null;
		}
		if (this.templateContainer) {
			this.templateContainer.clear();
		}
		// remove any injected iframe used for isolation rendering
		try {
			if (this._injectedInvoiceIframe && this._injectedInvoiceIframe.parentElement) {
				this._injectedInvoiceIframe.parentElement.removeChild(this._injectedInvoiceIframe);
			}
			this._injectedInvoiceIframe = null;
		} catch (e) {
			// ignore
		}
	}

	/**
	 * Fallback HTML generation for cases where dynamic rendering fails
	 */
	private generateFallbackHTML(invoiceData: any): string {
		const isHebrew = this.translateService.currentLang === 'he';
		const dir = isHebrew ? 'rtl' : 'ltr';
		
		const invoiceNumber = invoiceData?.document_number || invoiceData?.invoice_number || '113';
		const invoiceDate = invoiceData?.invoice_date || '17-09-2025';
		const customerName = invoiceData?.customer?.customer_name || 'נגיב יוסף';
		const customerVat = invoiceData?.customer?.customer_vat_number || '081013484';
		
		return `
			<div style="max-width: 800px; margin: 0 auto; background: #fff; padding: 40px; font-family: 'Arial', sans-serif; color: #333; direction: ${dir}; font-size: 14px; line-height: 1.6;">
				<div style="text-align: center; margin: 30px 0;">
					<div style="font-size: 18px; font-weight: bold; margin-bottom: 10px;">Invoice ${invoiceNumber}</div>
					<div style="margin-bottom: 20px;">Date: ${invoiceDate}</div>
					<div style="margin-bottom: 20px;">Customer: ${customerName}</div>
					<div>VAT: ${customerVat}</div>
				</div>
			</div>
		`;
	}
}
